import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../service/user.abstract.service';
import { takeUntil, tap, switchMap, map, catchError } from 'rxjs/operators';
import { Subject, Observable, of, ReplaySubject } from 'rxjs';
import { UserRequest, GetUserRequests } from '../../../user.model';
import { MatDialog } from '@angular/material/dialog';
import { UserRequestDetailComponent } from '../detail/user-request-detail.omponent';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { languageSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';

@Component({
    selector: 'app-user-requests',
    templateUrl: './user-requests.component.html',
    styleUrls: ['./user-requests.component.scss'],
})
export class UserRequestComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    pageUpdate$ = new ReplaySubject();

    loading = false;

    columnHeaders: any = {};
    actionMenu: any = {};
    userRequests: UserRequest[] = [];

    pageOptions: any = {
        page: 0,
        pageSize: 10,
    };

    dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' } as const;
    htmlProperties = ['isPublic'];

    requests$ = this.pageUpdate$.pipe(
        takeUntil(this.destroy$),
        tap(() => (this.loading = true)),
        switchMap(() =>
            this.userService.getRequests(this.pageOptions.page, this.pageOptions.pageSize, this.userid).pipe(
                takeUntil(this.destroy$),
                catchError((error) => {
                    this.messageService.error(this.translateService.instant('messages.error'));
                    return of({ docs: [], page: 1, totalDocs: 0, limit: 0 });
                }),
                map((response) => response),
            ),
        ),
    );

    constructor(
        public translateService: TranslateService,
        private userService: UserService,
        private dialog: MatDialog,
        private messageService: MessageService,
        private store: Store<State>,
        private route: ActivatedRoute,
    ) {}

    public id: string;
    public btnText: string;
    public btnIcon: string;
    public btnCommand: string;
    public userid: string;

    ngOnInit() {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            this.userid = params.get('id');
        });

        this.store
            .select(languageSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translateService.use(lang)),
                tap(() => {
                    this.columnHeaders = {
                        isPublic: this.translateService.instant('pages.user-requests.list.table.header.isPublic'),
                        requestNum: this.translateService.instant('pages.user-requests.list.table.header.requestNum'),
                        category: this.translateService.instant('pages.user-requests.list.table.header.category'),
                        subject: this.translateService.instant('pages.user-requests.list.table.header.subject'),
                        updated_at: this.translateService.instant('pages.user-requests.list.table.header.updated_at'),
                        status: this.translateService.instant('pages.user-requests.list.table.header.status'),
                    };
                    this.actionMenu = {
                        heading: this.translateService.instant('pages.user-requests.list.table.action-menu.heading'),
                        options: [
                            {
                                label: this.translateService.instant('pages.user-requests.list.table.action-menu.options.detail'),
                                eventEmitterTag: 'details',
                            },
                        ],
                    };
                }),
            )
            .subscribe();

        this.requests$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
            this.pageOptions = {
                ...this.pageOptions,
                page: response.page - 1,
                length: response.totalDocs,
                pageSize: response.limit,
            };
            this.userRequests = this.handleFormatRequests(response.docs);
            this.loading = false;
        });

        this.pageUpdate$.next();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleActions(e) {
        switch (e.tag) {
            case 'details':
                this.viewDetails(e.element.id);
                break;
            default:
                console.warn('unknown action');
                break;
        }
    }

    handleFormatRequests(requests: UserRequest[]) {
        const requestList = [];
        requests.forEach((req) => {
            const date = new Date(req.updated_at);
            const request = {
                id: req?._id,
                requestNum: req.requestNum,
                category: req.category.name,
                subject: req.subject.name,
                status: this.translateService.instant('pages.user-requests.common.request.status.' + req.status),
                isPublic: req.isPublic ? '<i class="icon-family"></i>' : '<i class="icon-view-off"></i>',
                updated_at: new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, this.dateOptions).format(date),
                userName: `${req?.user?.firstName} ${req?.user?.lastName}`,
            };
            requestList.push(request);
        });
        return requestList;
    }

    pageChange($event) {
        this.pageOptions = {
            ...this.pageOptions,
            page: $event.pageIndex,
            pageSize: $event.pageSize,
            length: $event.length,
        };
        this.pageUpdate$.next();
    }

    viewDetails(id: string) {
        this.dialog.open(UserRequestDetailComponent, { data: { id } });
    }
}
